/**
 * Date Time Picker Component
 * Step 2 of the booking widget - Select date and time
 */

import { useMemo } from 'react';

interface TimeSlot {
  date: string;
  time: string;
  available: boolean;
  serviceTypes: Array<{
    id: string;
    availableCount: number;
  }>;
}

interface DateTimePickerProps {
  slots: TimeSlot[];
  selectedServiceTypeId: string;
  selectedDate: string | null;
  selectedTime: string | null;
  onSelect: (date: string, time: string) => void;
  language?: 'es' | 'en';
  primaryColor?: string;
}

export function DateTimePicker({
  slots,
  selectedServiceTypeId,
  selectedDate,
  selectedTime,
  onSelect,
  language = 'es',
  primaryColor = '#2563eb',
}: DateTimePickerProps) {
  // Group slots by date
  const slotsByDate = useMemo(() => {
    const grouped = new Map<string, TimeSlot[]>();
    for (const slot of slots) {
      const dateSlots = grouped.get(slot.date) || [];
      dateSlots.push(slot);
      grouped.set(slot.date, dateSlots);
    }
    return grouped;
  }, [slots]);

  // Get unique dates
  const dates = useMemo(() => Array.from(slotsByDate.keys()).sort(), [slotsByDate]);

  // Get slots for selected date, filtered by service type availability
  const timeSlotsForDate = useMemo(() => {
    if (!selectedDate) return [];
    const dateSlots = slotsByDate.get(selectedDate) || [];
    return dateSlots.map((slot) => {
      const serviceAvailability = slot.serviceTypes.find((st) => st.id === selectedServiceTypeId);
      return {
        ...slot,
        available: slot.available && (serviceAvailability?.availableCount || 0) > 0,
      };
    });
  }, [selectedDate, slotsByDate, selectedServiceTypeId]);

  // Format date for display
  const formatDateLabel = (dateStr: string) => {
    const date = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (date.getTime() === today.getTime()) {
      return language === 'es' ? 'Hoy' : 'Today';
    }
    if (date.getTime() === tomorrow.getTime()) {
      return language === 'es' ? 'Mañana' : 'Tomorrow';
    }

    return date.toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    });
  };

  return (
    <div className="space-y-3">
      <h3 className="text-base font-semibold text-gray-900">
        {language === 'es' ? 'Selecciona fecha y hora' : 'Select date and time'}
      </h3>

      {/* Date selector */}
      <div className="flex gap-1.5 overflow-x-auto pb-1.5 scrollbar-hide">
        {dates.map((date) => {
          const dateSlots = slotsByDate.get(date) || [];
          const hasAvailable = dateSlots.some((slot) => {
            const serviceAvailability = slot.serviceTypes.find(
              (st) => st.id === selectedServiceTypeId
            );
            return slot.available && (serviceAvailability?.availableCount || 0) > 0;
          });

          return (
            <button
              key={date}
              onClick={() => onSelect(date, '')}
              disabled={!hasAvailable}
              className={`flex-shrink-0 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors ${
                selectedDate === date
                  ? 'text-white'
                  : hasAvailable
                    ? 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                    : 'bg-gray-50 text-gray-400 cursor-not-allowed'
              }`}
              style={selectedDate === date ? { backgroundColor: primaryColor } : undefined}
            >
              {formatDateLabel(date)}
            </button>
          );
        })}
      </div>

      {/* Time slots */}
      {selectedDate && (
        <div className="grid grid-cols-4 sm:grid-cols-5 gap-1.5">
          {timeSlotsForDate.map((slot) => {
            const isSelected = selectedTime === slot.time && selectedDate === slot.date;
            return (
              <button
                key={`${slot.date}-${slot.time}`}
                onClick={() => slot.available && onSelect(selectedDate, slot.time)}
                disabled={!slot.available}
                className={`px-2 py-1.5 rounded-md text-xs font-medium transition-colors ${
                  isSelected
                    ? 'text-white'
                    : slot.available
                      ? 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                      : 'bg-gray-50 text-gray-400 cursor-not-allowed line-through'
                }`}
                style={isSelected ? { backgroundColor: primaryColor } : undefined}
              >
                {slot.time}
              </button>
            );
          })}
        </div>
      )}

      {selectedDate && timeSlotsForDate.length === 0 && (
        <p className="text-gray-500 text-xs">
          {language === 'es'
            ? 'No hay horarios disponibles para esta fecha'
            : 'No available times for this date'}
        </p>
      )}
    </div>
  );
}
